<script setup>
import { ref } from 'vue'
import { requiredValidator } from '../../../utils/validators'
import { sanitizeHtml } from '@core/utils/sanitizeHtml'
import { useTagsStore } from '@/stores'
import { useDesignSystem } from '@/composables/useDesignSystem'

const props = defineProps({
  isDialogVisible: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(['update:isDialogVisible'])
const { dialogWidth } = useDesignSystem()
const dWidth = dialogWidth('md')

// Tiptap
const vuetifyTiptapRef = ref(null)
const output = ref('html')
const markdownTheme = ref('')
const outlined = ref(true)
const dense = ref(false)
const hideToolbar = ref(false)
const disableToolbar = ref(false)
const errorMessages = ref(null)
const maxWidth = ref(900)

function onSaveLongDescription({ editor, output }) {
  let editorContent = editor.getHTML()
      
  // Remove <p> and </p> tags from the content
  let cleanedContent = editorContent.replace(/<\/?p>/g, '')
        
  // Set cleaned content back or save it
  newTag.value.tagDescription = cleanedContent
}

function onSaveShortDescription({ editor, output }) {
  let editorContent = editor.getHTML()
      
  // Remove <p> and </p> tags from the content
  let cleanedContent = editorContent.replace(/<\/?p>/g, '')
        
  // Set cleaned content back or save it
  newTag.value.tagShortDescription = cleanedContent
}

const initialData = {
  tagName: '',
  tagDescription: '',
  tagShortDescription: '',
  status: 'approved',
}

const tagsStore = useTagsStore()
const form = ref()
const newTag = ref(structuredClone(initialData))
const updateModelValue = val => {
  emit('update:isDialogVisible', val)
}

const onFormSubmit = async () => {
  form.value.validate().then(async ({valid: isValid}) => {
    if(isValid){
      onSaveLongDescription({ editor: vuetifyTiptapRef.value.editor })
      onSaveShortDescription({ editor: vuetifyTiptapRef.value.editor })
      await tagsStore.createTag(newTag.value)
      await tagsStore.getTags()
      newTag.value = initialData
      emit('update:isDialogVisible', false)
    }
  })
}
</script>

<template>
  <VDialog
    :model-value="isDialogVisible"
    :width="dWidth"
    @update:model-value="updateModelValue"
  >
    <VCard class="pa-sm-9 pa-5">
      <VCardItem class="text-center">
        <VCardTitle class="text-h5">
          Tag (New)
        </VCardTitle>
        <VCardSubtitle>
          <VMenu
            offset-y
            open-on-hover
            open-on-click
            close-on-content-click
          >
            <template #activator="{ props }">
              <VChip
                v-bind="props"
                size="small"
                class="flex-shrink-0"
              >
                {{ newTag.tagName }}
              </VChip>
            </template>
            <VCard class="verified-tag-popup">
              <VCardText
                v-if="newTag.tagDescription"
                v-html="sanitizeHtml(newTag.tagDescription)"
              />
              <VCardText v-else>
                Verified by RM
              </VCardText>
            </VCard>
          </VMenu>
        </VCardSubtitle>
      </VCardItem>
      <VCardText>
        <VForm
          ref="form"
          class="mt-6"
          @submit.prevent="onFormSubmit"
        >
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="newTag.tagName"
                label="Tag Name"
                autocomplete="null"
                :rules="[requiredValidator]"
              />
            </VCol>
            <VCol cols="12">
              <VSelect
                v-model="newTag.status"
                label="Status"
                :items="[{title:'Approved', value: 'approved'},{title:'Pending', value: 'pending'}]"
              />
            </VCol>
            <VCol cols="12">
              <VuetifyTiptap
                ref="vuetifyTiptapRef"
                v-model="newTag.tagDescription"
                v-model:markdown-theme="markdownTheme"
                label="Long Description"
                :output="output"
                :hide-toolbar="hideToolbar"
                :disable-toolbar="disableToolbar"
                :outlined="outlined"
                :dense="dense"
                :error-messages="errorMessages"
                rounded
                :max-height="465"
                :max-width="maxWidth"
              />
            </VCol>
            <VCol cols="12">
              <VuetifyTiptap 
                ref="vuetifyTiptapRef"
                v-model="newTag.tagShortDescription"
                v-model:markdown-theme="markdownTheme"
                label="Short Description"
                :output="output"
                :hide-toolbar="hideToolbar"
                :disable-toolbar="disableToolbar"
                :outlined="outlined"
                :dense="dense"
                :error-messages="errorMessages"
                rounded
                :max-height="465"
                :max-width="maxWidth"
              />
            </VCol>
            <!-- 👉 Submit and Cancel -->
            <VCol
              cols="12"
              class="d-flex flex-wrap justify-center gap-4"
            >
              <VBtn
                v-if="true"
                @click="form?.validate(); onFormSubmit()"
              >
                Save
              </VBtn>
              <VBtn
                color="secondary"
                variant="tonal"
                @click="emit('update:isDialogVisible', false); newTag = initialData"
              >
                Cancel
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>
    </VCard>
  </VDialog>
</template>